import { useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { BottomNavigation } from './components/bottom-navigation/BottomNavigation';
import { GlobalTableSessionBar } from './components/session';
import { useRestaurants, useReceiptActions, useOrderCompletion } from './hooks';
import { useActiveOrders } from './hooks/orders/useActiveOrders';
import { AppHeader, FullPageRouter, AppModals, AppRoutes } from './components/layout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ReConsentBanner } from '@/components/ReConsentBanner';

// Context 훅 사용
import { useApp } from '@/contexts/AppContext';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationsContext';
import { useOrderHistory } from '@/contexts/OrderHistoryContext';
import {
  useCheckout,
  usePaymentFlow,
  useTableOrder,
  useInvoice,
} from '@/contexts/CheckoutContext';

// 분리된 모듈 import
import { formatCurrency } from '@/utils';

// 유틸리티 함수 import
import {
  calculateCartTotal,
  getAppShellViewportClasses,
  getHeaderTitle,
  isAnyModalOpen,
  isViewOnlyMode,
  isWaitlistPath,
} from '@/lib/app-utils';
import { navigationEvents } from '@/lib/navigation-events';
import {
  ROUTES,
  getRestaurantTableCheckoutPath,
  getTabFromPath,
  isCheckoutPath,
} from '@/config/routes';
import { logger } from '@/utils/logger';

/**
 * App 레벨 에러 Fallback UI
 *
 * App 컴포넌트 내부에서 발생하는 렌더링 에러를 처리합니다.
 * 루트 레벨의 Sentry ErrorBoundary와 함께 2단계 에러 처리를 제공합니다.
 */
function AppErrorFallback({ error, resetError }: { error: Error | null; resetError: () => void }) {
  const { language } = useApp();

  const errorTranslations = {
    ko: {
      title: '앱 오류가 발생했습니다',
      message: '죄송합니다. 일시적인 오류가 발생했습니다.',
      retry: '다시 시도',
      refresh: '페이지 새로고침',
    },
    en: {
      title: 'App Error Occurred',
      message: 'Sorry, a temporary error occurred.',
      retry: 'Try Again',
      refresh: 'Refresh Page',
    },
    es: {
      title: 'Error de la aplicación',
      message: 'Lo sentimos, ha ocurrido un error temporal.',
      retry: 'Intentar de nuevo',
      refresh: 'Actualizar página',
    },
  };

  const t = errorTranslations[language] || errorTranslations.en;

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-muted">
      <div className="text-center max-w-[400px] p-6">
        {/* 에러 아이콘 */}
        <div className="mb-4">
          <svg
            className="w-16 h-16 mx-auto text-destructive"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        <h2 className="text-xl font-bold text-foreground mb-3">{t.title}</h2>
        <p className="text-sm text-muted-foreground mb-5">{t.message}</p>

        {/* 개발 환경에서만 에러 메시지 표시 */}
        {import.meta.env.DEV && error && (
          <pre className="text-left bg-destructive/10 border border-destructive/30 text-destructive p-3 rounded-lg text-xs mb-5 overflow-auto max-h-32">
            {error.message}
          </pre>
        )}

        {/* 버튼 그룹 */}
        <div className="flex flex-col gap-3">
          <button
            onClick={resetError}
            className="w-full bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-hover transition-colors"
          >
            {t.retry}
          </button>
          <button
            onClick={handleRefresh}
            className="w-full bg-muted text-muted-foreground px-6 py-3 rounded-lg font-medium hover:bg-muted/80 transition-colors"
          >
            {t.refresh}
          </button>
        </div>
      </div>
    </div>
  );
}

function AppContent() {
  // URL 기반 라우팅
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // #541: 익명 순수 메뉴판 모드 (?mode=view).
  // QRVerifyPage 가 view 타입 QR 응답 시 /restaurant/{id}?mode=view 로 진입.
  // 전역 하단 네비(TabNav) + 로그인 헤더 미렌더 게이트.
  // `/restaurant` 경로로 스코프 — 다른 경로가 향후 ?mode= 쿼리를 써도 영향 0.
  const isViewOnly = useMemo(
    () => isViewOnlyMode(location.pathname, searchParams.get('mode')),
    [location.pathname, searchParams],
  );

  // Context에서 전역 상태 가져오기
  // #473 (R1): setCurrency destructuring 제거 — context 자체에서 제거됨.
  const { locale, language, setLanguage, currency, t } = useApp();
  const {
    cartItems,
    setCartItems,
    addToCart: handleAddToCart,
    cartItemCount,
    cartTotal,
  } = useCart();
  const { isLoggedIn, isCheckingSession, setIsLoggedIn, user, customerId } = useAuth();

  // activeTab은 URL에서 파생 (더 이상 상태로 관리하지 않음)
  const activeTab = useMemo(() => getTabFromPath(location.pathname), [location.pathname]);
  const isCheckoutRoute = useMemo(() => isCheckoutPath(location.pathname), [location.pathname]);
  // #781: 줄서기(Fila Virtual)는 QR 전용 진입 화면으로 자체 AppBar(매장명 + 15개 언어
  // 선택)와 자체 하단 CTA 를 갖는다. 전역 헤더/하단 네비를 겹치면 헤더가 이중이 되고,
  // 아직 주문 맥락이 없는 대기 고객에게 장바구니/탭 진입 동선이 노출된다.
  const isWaitlistRoute = useMemo(() => isWaitlistPath(location.pathname), [location.pathname]);
  const appShellViewportClasses = getAppShellViewportClasses(isWaitlistRoute);
  const showGlobalBottomChrome = !isViewOnly &&
    !isWaitlistRoute &&
    location.pathname !== ROUTES.CART &&
    !isCheckoutRoute &&
    !location.pathname.startsWith('/payment') &&
    !location.pathname.startsWith('/table-session');

  // setActiveTab은 이제 navigate로 대체
  const setActiveTab = useCallback((tab: string) => {
    const routes: Record<string, string> = {
      home: '/',
      orders: '/orders',
      qr: '/qr',
      points: '/points',
      admin: '/admin',
    };
    navigate(routes[tab] || '/');
  }, [navigate]);

  // (#414) currentTable local state 제거 — 어디서도 실제 값이 set 되지 않던 dead state.
  // 테이블 컨텍스트 단일 출처는 URL/sessionStorage 파생 (useRestaurants + src/lib/tableContext.ts 박제 D).

  // useOrderHistory 훅 사용
  const {
    orderHistory,
    selectedOrderForReceipt,
    setSelectedOrderForReceipt,
    createOrderFromCheckout,
    guestCredentials,
  } = useOrderHistory();

  // /orders 탭 — 진행 중(non-terminal) 주문 추적.
  // #659: order-history 와 데이터 소스 공유 합성 (3소스 — 합성 규칙은 훅 docblock 참조):
  //   - 자격증명 조회: 로그인 GET /orders (Sanctum) / 비회원 guestLookup (phone+password)
  //   - 테이블 세션 토큰 조회: GET /table-sessions/{token}/unpaid-postpay (admin R5)
  //   - 로컬 주문 스냅샷(OrderHistoryContext) 폴백: 서버 status 검증 후 활성 건만
  const activeOrdersMode = isLoggedIn ? 'auth' : guestCredentials ? 'guest' : 'disabled';
  const {
    orders: activeOrders,
    loading: activeOrdersLoading,
    error: activeOrdersError,
    authRequired: activeOrdersAuthRequired,
  } = useActiveOrders({
    mode: activeOrdersMode,
    customerId: user?.id ?? customerId,
    guestCredentials,
    localSnapshots: orderHistory,
    pollingEnabled: activeTab === 'orders',
  });

  // useRestaurants 훅 사용
  const {
    selectedRestaurant,
    showPickupMenu,
    showTableOrderMenu,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    isLoading,
    restaurants,
    filteredRestaurants,
    mapStores,
    hasNoNearbyStores,
    apiError,
    handleFavoriteToggle,
    handleRestaurantSelect,
    handleStoreSelect,
    handleBackFromPickupMenu,
    handleBackFromTableOrderMenu,
    resetRestaurantState,
  } = useRestaurants({ language, translations: t });
  const checkoutPath = useMemo(() => {
    if (!showTableOrderMenu) {
      return ROUTES.CHECKOUT;
    }

    const tableRouteMatch = location.pathname.match(/^\/restaurant\/([^/]+)\/table\/([^/]+)/);
    if (!tableRouteMatch) {
      return ROUTES.CHECKOUT;
    }

    return getRestaurantTableCheckoutPath(tableRouteMatch[1], tableRouteMatch[2]);
  }, [location.pathname, showTableOrderMenu]);

  // useNotifications 훅 사용 - 표시 상태는 AppModals 내부에서 관리
  const { unreadCount } = useNotifications();

  // #43: useCheckout에서 테이블 주문 및 결제 상태 통합 관리
  // 선택적 훅으로 분리 (선택적 상태만 사용으로 리렌더링 최적화)
  const paymentFlowState = usePaymentFlow();
  const tableOrderState = useTableOrder();
  const invoiceState = useInvoice();
  const { resetCheckoutState } = useCheckout();

  // 결제 관련 상태 (AppModals에서 Context로 직접 접근하므로 최소한만 유지)
  const {
    showPayment,
    paymentFlow,
    setPaymentFlow,
    orderData,
    setOrderData,
    handlePaymentSuccess,
    handlePaymentClose,
  } = paymentFlowState;

  // 테이블 주문 관련 상태
  const {
    orderedItems,
    expandedOrders,
    addOrderedItems,
    toggleOrderExpand,
    orderedItemCount,
  } = tableOrderState;

  // 세금계산서 관련 상태 (AppModals에서 Context로 직접 접근하므로 최소한만 유지)
  const {
    setInvoiceData,
    showCompanyList,
    handleCompanyListBack,
    handleCompanySelect,
  } = invoiceState;

  // 별칭 (기존 코드 호환)
  const resetTableOrderState = resetCheckoutState;
  const resetPaymentState = resetCheckoutState;

  // Checkout handler - 체크아웃 페이지로 이동
  // 참고: Cart 닫기는 AppModals 내부에서 handleCheckout 호출 전에 처리됨
  const handleCheckout = useCallback(() => {
    const allItems = showTableOrderMenu ? [...orderedItems, ...cartItems] : cartItems;
    logger.info('Proceeding to checkout with items:', allItems);
    navigate(checkoutPath); // 체크아웃 페이지로 이동
  }, [showTableOrderMenu, orderedItems, cartItems, navigate, checkoutPath]);

  // 테이블 주문 완료 - Cart 닫기는 AppModals 내부에서 처리됨
  const handleTableOrderComplete = useCallback(() => {
    logger.info('Table order placed:', cartItems);
    addOrderedItems(cartItems);
    setCartItems([]);
  }, [cartItems, addOrderedItems, setCartItems]);

  // handleReceiptClose - 주문 완료 후 상태 초기화 (훅으로 분리)
  const handleReceiptClose = useOrderCompletion({
    selectedRestaurant,
    orderData,
    cartItems,
    orderedItems,
    showTableOrderMenu,
    getRestaurantName: (nameKey: string) => t.restaurants[nameKey]?.name ?? '',
    createOrderFromCheckout,
    setCartItems,
    resetPaymentState,
    resetTableOrderState,
    resetRestaurantState,
    setActiveTab,
  });

  // 네비게이션 이벤트 리스너 (직접 처리)
  useEffect(() => {
    const handleNavigateToPickupMenu = (event: CustomEvent) => {
      const { brandName } = event.detail;
      const restaurant = restaurants.find(r => t.restaurants[r.nameKey]?.name === brandName);
      if (restaurant && restaurant.isOpen) {
        handleRestaurantSelect(restaurant.id);
      }
    };
    const handleNavigateToHome = () => navigate('/');
    const handleNavigateToFranchiseStores = () => navigate('/points');

    window.addEventListener('navigateToPickupMenu', handleNavigateToPickupMenu as EventListener);
    window.addEventListener('navigateToHome', handleNavigateToHome);
    window.addEventListener('navigateToFranchiseStores', handleNavigateToFranchiseStores);

    return () => {
      window.removeEventListener('navigateToPickupMenu', handleNavigateToPickupMenu as EventListener);
      window.removeEventListener('navigateToHome', handleNavigateToHome);
      window.removeEventListener('navigateToFranchiseStores', handleNavigateToFranchiseStores);
    };
  }, [restaurants, t, handleRestaurantSelect, navigate]);

  // 헤더 타이틀 계산 (메모이제이션) — #536 full locale 전달 (ru → es 축약 방지).
  const headerTitle = useMemo(() => getHeaderTitle({
    showPickupMenu,
    showTableOrderMenu,
    selectedRestaurant,
    activeTab,
    locale,
    t,
  }), [showPickupMenu, showTableOrderMenu, selectedRestaurant, activeTab, locale, t]);

  // 장바구니 아이템 및 총합 계산 (단일 메모이제이션)
  const { allCartItems, allCartTotal } = useMemo(() => {
    const items = showTableOrderMenu ? [...orderedItems, ...cartItems] : cartItems;
    return { allCartItems: items, allCartTotal: calculateCartTotal(items) };
  }, [showTableOrderMenu, orderedItems, cartItems]);

  // useReceiptActions 훅 사용
  const {
    handleReceiptBackFromHistory: receiptBackHandler,
    handleViewReceiptFromHistory,
  } = useReceiptActions({
    setOrderData,
    setPaymentFlow,
    setSelectedOrderForReceipt,
    setShowOrderHistory: () => {}, // FullPageRouter 내부에서 관리 (no-op)
    setInvoiceData,
    onReceiptClose: handleReceiptClose,
  });

  // 영수증 뒤로가기 핸들러 (selectedOrderForReceipt 전달)
  const handleReceiptBackFromHistory = useCallback(() =>
    receiptBackHandler(selectedOrderForReceipt),
    [receiptBackHandler, selectedOrderForReceipt]
  );

  // 로그아웃 확인 핸들러
  const handleConfirmLogout = useCallback(() => {
    setIsLoggedIn(false);
    navigationEvents.openLoginPage();
  }, [setIsLoggedIn]);

  // 모달 열림 상태 계산 (메모이제이션)
  // 참고: Cart/Notifications/Settings/Map/Logout 모달은 AppModals 내부에서 관리됨
  const modalOpen = useMemo(() => isAnyModalOpen({
    paymentFlow,
  }), [paymentFlow]);

  return (
    <FullPageRouter
      showPayment={showPayment}
      onPaymentClose={handlePaymentClose}
      onPaymentSuccess={handlePaymentSuccess}
      allCartItems={allCartItems}
      allCartTotal={allCartTotal}
      showTableOrderMenu={showTableOrderMenu}
      showCompanyList={showCompanyList}
      onCompanyListBack={handleCompanyListBack}
      onCompanySelect={handleCompanySelect}
      paymentFlow={paymentFlow}
      orderData={orderData}
      onReceiptBack={handleReceiptBackFromHistory}
      orderHistory={orderHistory}
      onViewReceipt={handleViewReceiptFromHistory}
    >
    <div
      className={`${appShellViewportClasses.root} bg-muted text-foreground font-['Noto_Sans',sans-serif]`}
    >
      {/* #781: 줄서기 화면은 자체 AppBar 를 갖는다 — 전역 헤더 미렌더 + 상단 여백 제거. */}
      {!isWaitlistRoute && (
      <AppHeader
        activeTab={activeTab}
        showPickupMenu={showPickupMenu}
        showTableOrderMenu={showTableOrderMenu}
        isLoggedIn={isLoggedIn}
        isCheckingSession={isCheckingSession}
        isBlurred={modalOpen}
        headerTitle={headerTitle}
        unreadNotificationCount={unreadCount}
        isViewOnly={isViewOnly}
        onBackFromPickup={handleBackFromPickupMenu}
        onBackFromTableOrder={handleBackFromTableOrderMenu}
        onBackToHome={() => navigate('/')}
      />
      )}

      <div className={`${isWaitlistRoute ? '' : 'pt-[50px]'} ${modalOpen ? 'blur-sm' : ''} transition-all duration-300`}>
        {activeTab === 'home' && cartItemCount > 0 && (
          <div className="fixed top-[calc(4rem+1vw)] right-[min(5vw,1.25rem)] z-40"></div>
        )}

        <main className={`flex flex-col ${appShellViewportClasses.main}`}>
          <AppRoutes
            // 메인 레이아웃 props
            showPickupMenu={showPickupMenu}
            showTableOrderMenu={showTableOrderMenu}
            selectedRestaurant={selectedRestaurant}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            sortBy={sortBy}
            onSortChange={setSortBy}
            filteredRestaurants={filteredRestaurants}
            isLoading={isLoading}
            hasNoNearbyStores={hasNoNearbyStores}
            apiError={apiError}
            restaurants={restaurants}
            onFavoriteToggle={handleFavoriteToggle}
            onRestaurantSelect={handleRestaurantSelect}
            onBackFromPickup={handleBackFromPickupMenu}
            onBackFromTableOrder={handleBackFromTableOrderMenu}
            onAddToCart={handleAddToCart}
            orderedItemCount={orderedItemCount}
            onPaymentClick={handleCheckout}
            orders={activeOrders}
            activeOrdersLoading={activeOrdersLoading}
            activeOrdersError={activeOrdersError}
            activeOrdersAuthRequired={activeOrdersAuthRequired}
            expandedOrders={expandedOrders}
            onToggleExpand={toggleOrderExpand}
            isLoggedIn={isLoggedIn}
            isCheckingSession={isCheckingSession}
            onLanguageChange={setLanguage}
            // 전체 페이지 props
            showPayment={showPayment}
            onPaymentClose={handlePaymentClose}
            onPaymentSuccess={handlePaymentSuccess}
            allCartItems={allCartItems}
            allCartTotal={allCartTotal}
            showCompanyList={showCompanyList}
            onCompanyListBack={handleCompanyListBack}
            onCompanySelect={handleCompanySelect}
            paymentFlow={paymentFlow}
            orderData={orderData}
            onReceiptBack={handleReceiptBackFromHistory}
            orderHistory={orderHistory}
            onViewReceipt={handleViewReceiptFromHistory}
            // 인증 관련 props (FullPageRouter에서 사용되는 것들)
            verifiedEmail=""
            verifiedCompany=""
            verifiedBirthday=""
            onEmailVerificationComplete={() => {}}
            onBirthdayVerificationComplete={() => {}}
            onLogin={() => {}}
            onNavigateFromProfileToEmailVerification={() => navigate('/verify/email')}
            // 추가 props
            onLocationClick={navigationEvents.openMapModal}
          />
        </main>

        {/* Cart, Checkout 및 Payment 페이지에서는 하단 네비와 세션 바를 함께 숨김.
            #541: view 모드(익명 순수 메뉴판)에서도 하단 TabNav(계정/설정 진입) 미렌더.
            #660: 활성 테이블 세션 글로벌 스테이터스 레이어 — 하단 네비 위 고정, 탭 시
            테이블 오더 페이지 복귀. 네비와 동일 게이트(checkout/payment/view 제외)로 렌더하고,
            테이블 페이지 중복 억제·만료 자동 제거는 컴포넌트 내부에서 판정한다.
            #661: 세션 전체 결제 전용 페이지(/table-session/charge)는 결제 집중 흐름이라
            네비·세션 바 모두 숨김 — 이미 해당 세션을 결제 중이라 세션 바 진입 동선이 불필요. */}
        {showGlobalBottomChrome && (
          <>
            <GlobalTableSessionBar />
            <BottomNavigation
              activeTab={activeTab}
              onTabChange={setActiveTab}
              showPickupMenu={showPickupMenu || showTableOrderMenu}
              cartTotal={formatCurrency(cartTotal, currency)}
              cartItemCount={cartItemCount}
              onCartClick={navigationEvents.toggleCart}
              mode={showTableOrderMenu ? 'table' : 'pickup'}
              orderedItemCount={orderedItemCount}
              onPaymentClick={handleCheckout}
            />
          </>
        )}
      </div>

      <AppModals
        isTableOrderMode={showTableOrderMenu}
        onCheckout={handleCheckout}
        onTableOrderComplete={handleTableOrderComplete}
        stores={mapStores}
        onStoreSelect={handleStoreSelect}
        onConfirmLogout={handleConfirmLogout}
      />

      {/* LFPDPPP 재동의 배너 (#372) — 모든 인증 필요 페이지에서 표시.
          Primary 거부 시 Aviso 페이지 (/privacy) 로 이동 (양 채널 1R P1-1 시정). */}
      <ReConsentBanner onShowAviso={() => navigate('/privacy')} />
    </div>
    </FullPageRouter>
  );
}

/**
 * App 컴포넌트 with ErrorBoundary
 *
 * 메인 앱 컴포넌트에 ErrorBoundary를 적용하여
 * 컴포넌트 트리 내부에서 발생하는 렌더링 에러를 캐치합니다.
 *
 * 루트 레벨(main.tsx)의 Sentry ErrorBoundary와 함께
 * 2단계 에러 처리 계층을 형성합니다.
 */
export default function App() {
  return (
    <ErrorBoundary
      fallback={({ error, resetError }) => (
        <AppErrorFallback error={error} resetError={resetError} />
      )}
      onError={(error, errorInfo) => {
        logger.error('[App ErrorBoundary]', error, {
          componentStack: errorInfo.componentStack,
        });
      }}
    >
      <AppContent />
    </ErrorBoundary>
  );
}
